import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Trash2, Link2, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const InvitationsTab = ({ ws }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [expiresDays, setExpiresDays] = useState("7");
  const [creating, setCreating] = useState(false);

  const { data: invitations = [] } = useQuery({
    queryKey: ["invitations", ws.id],
    queryFn: async () => (await api.get(`/workspaces/${ws.id}/invitations`)).data,
  });

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const create = async (withEmail) => {
    if (withEmail && !email.trim()) return toast.error("Introduce un correo electrónico");
    setCreating(true);
    try {
      const { data } = await api.post(`/workspaces/${ws.id}/invitations`, {
        email: withEmail ? email.trim() : null,
        role,
        expires_in_days: parseInt(expiresDays),
      });
      qc.invalidateQueries({ queryKey: ["invitations", ws.id] });
      qc.invalidateQueries({ queryKey: ["audit", ws.id] });
      await navigator.clipboard.writeText(inviteUrl(data.token)).catch(() => {});
      toast.success(
        withEmail
          ? data.email_sent
            ? `Invitación enviada por email a ${email}. Enlace copiado también.`
            : `Invitación creada para ${email}. Enlace copiado.`
          : "Enlace de invitación creado y copiado"
      );
      setEmail("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token) => {
    await navigator.clipboard.writeText(inviteUrl(token)).catch(() => {});
    toast.success("Enlace copiado al portapapeles");
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/workspaces/${ws.id}/invitations/${id}`);
      qc.invalidateQueries({ queryKey: ["invitations", ws.id] });
      toast.success("Invitación revocada");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-invitations-tab">
      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-semibold">Invitar personas</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-2 sm:col-span-1">
            <Label>Correo (opcional)</Label>
            <Input data-testid="invitation-email-input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="persona@correo.com" />
          </div>
          <div className="space-y-2">
            <Label>Rol inicial</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invitation-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{ws.role_labels?.admin || "Administrador"}</SelectItem>
                <SelectItem value="member">{ws.role_labels?.member || "Miembro"}</SelectItem>
                <SelectItem value="guest">{ws.role_labels?.guest || "Invitado"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Caducidad</Label>
            <Select value={expiresDays} onValueChange={setExpiresDays}>
              <SelectTrigger data-testid="invitation-expiry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 día</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button data-testid="invitation-create-email-btn" onClick={() => create(true)} disabled={creating}>
            <Mail className="h-4 w-4 mr-2" /> Invitar por correo
          </Button>
          <Button variant="outline" data-testid="invitation-create-link-btn" onClick={() => create(false)} disabled={creating}>
            <Link2 className="h-4 w-4 mr-2" /> Generar enlace de invitación
          </Button>
        </div>
      </Card>

      <div className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Invitaciones pendientes</h3>
        {invitations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay invitaciones pendientes.</p>
        ) : (
          <Card className="divide-y divide-border">
            {invitations.map((inv) => (
              <div key={inv.id} className="p-4 flex items-center gap-3" data-testid={`invitation-row-${inv.id}`}>
                {inv.email ? <Mail className="h-4 w-4 text-muted-foreground shrink-0" /> : <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.email || "Enlace de invitación"}</p>
                  <p className="text-xs text-muted-foreground">
                    {ws.role_labels?.[inv.role] || inv.role} · creada {timeAgo(inv.created_at)} · caduca{" "}
                    {new Date(inv.expires_at).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                  </p>
                </div>
                <Badge variant="secondary" className="shrink-0 hidden sm:inline-flex">Pendiente</Badge>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`invitation-copy-btn-${inv.id}`}
                  onClick={() => copyLink(inv.token)}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" data-testid={`invitation-revoke-btn-${inv.id}`}
                  onClick={() => revoke(inv.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
};
